import { Pencil, Trash2, ClipboardCheck } from "lucide-react";

import CustomProductName from "@/components/desktop/products/product_name/CustomProductName";
import ExpandableTable from "@/components/desktop/list/table/ExpandableTable";
import CustomButton from "@/components/desktop/button/CustomButton";
import CustomBadge from "@/components/desktop/badge/CustomBadge";

import useFilterStore from "@/stores/useFilterStore";

import { convertValue } from "@/utils/unitConverter";
import { getUnitLabel } from "@/utils/unitFormatter";
import { formatNumber, truncateToDecimals } from "@/utils/numberFormat";

import { QUANTITY_DECIMALS } from "@/components/global.constants";

const OutputHistoryDetails = ({ items, onEdit, onDelete, onDistribution }) => {
	const { filter } = useFilterStore();
	const unitMeasurement = filter?.unit_measurement || "kg_l";
	const canEdit = Boolean(onEdit);
	const canDelete = Boolean(onDelete);
	const canDistribute = Boolean(onDistribution);

	const columns = [
		{ key: "created_time", label: "Hora Salida" },
		{ key: "code", label: "Código" },
		{
			key: "product_name",
			label: "Producto",
			render: (value, row) => {
				return (
					<CustomProductName
						product={{
							code: row.product_code,
							value: row.product_code,
							label: row.product_name,
						}}
					/>
				);
			},
		},
		{
			key: "quantity_output",
			label: "Cantidad Salida",
			align: "right",
			render: (value, row) => {
				if (row.unit_measurement === "u") {
					return formatNumber(value, 0);
				}
				const convertedValue = convertValue(value, row.unit_measurement, unitMeasurement);
				return (
					<div style={{ fontWeight: 500 }}>
						{formatNumber(truncateToDecimals(convertedValue, QUANTITY_DECIMALS), QUANTITY_DECIMALS) +
							" " +
							getUnitLabel(row.unit_measurement, unitMeasurement)}
					</div>
				);
			},
		},
		{
			key: "business_center_name",
			label: "Centro Costo",
			render: (value) => {
				if (!value) return <span>-</span>;

				const centers = value.split(",").map((center) => center.trim());

				return (
					<div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
						{centers.map((center, index) => (
							<CustomBadge key={index} variant="neutral" size="sm">
								{center}
							</CustomBadge>
						))}
					</div>
				);
			},
		},
	];

	if (canEdit || canDelete || canDistribute) {
		columns.push({
			key: "actions",
			label: "Acciones",
			align: "center",
			render: (value, row) => {
				return (
					<div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
						{canDistribute && (
							<CustomButton
								variant="secondary"
								size="sm"
								onClick={() => onDistribution(row.application_id)}
								startIcon={<ClipboardCheck size={16} />}
								title={"Revisar centros de costo"}
								style={{ padding: "0.5rem" }}
							/>
						)}
						{canEdit && (
							<CustomButton
								variant="primary"
								size="sm"
								onClick={() => onEdit(row.application_id)}
								startIcon={<Pencil size={16} />}
								title={"Editar aplicación"}
								style={{ padding: "0.5rem" }}
							/>
						)}
						{canDelete && (
							<CustomButton
								variant="danger"
								size="sm"
								onClick={() => onDelete(row.application_id)}
								startIcon={<Trash2 size={16} />}
								title={"Eliminar aplicación"}
								style={{ padding: "0.5rem" }}
							/>
						)}
					</div>
				);
			},
		});
	}

	return (
		<ExpandableTable title={`Productos`} subtitle={`Total de Productos: ${items.length}`} columns={columns} data={items} rowKey="code" fullWidth={true} />
	);
};

export default OutputHistoryDetails;
